import { useApiGetTeamListUsers } from '@repo/api-codegen';
import { useApiListTeamsV2 } from '@repo/api-codegen/v2';
import { isEmpty } from 'lodash-es';
import { useMemo } from 'react';
import { useUserGroupList } from '../userGroup';
import { useSuspenseFeatureAccess } from '../workspace';
import { useUserList } from './index';

export function useGuestUserList() {
	const { data: JoinedTeams, isLoading: isJoinedTeamsLoading } =
		useApiListTeamsV2({
			queryParams: {
				only_joined: true,
			},
		});

	const teamId = JoinedTeams?.[0]?.id || '';

	const {
		data: usersVisibleToGuests,
		isLoading: isUsersVisibleToGuestsLoading,
	} = useApiGetTeamListUsers(
		{
			pathParams: {
				teamId,
			},
		},
		{
			select: ({ results }) => results,
			enabled: !isEmpty(teamId),
		}
	);

	return {
		usersVisibleToGuests,
		isLoading: isJoinedTeamsLoading || isUsersVisibleToGuestsLoading,
	};
}

export function useExtendedUserList() {
	const { groupAccess } = useSuspenseFeatureAccess();

	const { data: userGroups, isLoading: isUserGroupsLoading } = useUserGroupList(
		{
			options: {
				select: ({ results }) => results,
				enabled: !!groupAccess,
			},
		}
	);

	const { data: users, isLoading: isUsersLoading } = useUserList({
		options: {
			select: ({ results }) => results,
		},
	});

	const activeUsers = useMemo(
		() =>
			users?.filter(
				(user) => !user.disabled && !user.is_service_account && !user.pending
			) || [],
		[users]
	);
	const disabledUsers = useMemo(
		() =>
			users?.filter((user) => user.disabled && !user.is_service_account) || [],
		[users]
	);
	const pendingUsers = useMemo(
		() =>
			users?.filter(
				(user) => !user.disabled && user.pending && !user.is_service_account
			) || [],
		[users]
	);
	const serviceAccountsDisabled = useMemo(
		() =>
			users?.filter((user) => user.disabled && user.is_service_account) || [],
		[users]
	);
	const serviceAccountsActive = useMemo(
		() =>
			users?.filter((user) => !user.disabled && user.is_service_account) || [],
		[users]
	);

	return {
		userGroups: !groupAccess ? [] : userGroups,
		users: users ?? [],
		activeUsers,
		disabledUsers,
		pendingUsers,
		serviceAccountsActive,
		serviceAccountsDisabled,
		isLoading: (!groupAccess ? false : isUserGroupsLoading) || isUsersLoading,
	};
}
